import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  Button,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import api from "../../utils/api";
import {
  loadingAlert,
  closeAlert,
  confirmAlert,
  errorAlert,
  successAlert,
} from "../../utils/swalConfig";

const formatDateTime = (dateString) => {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

const BankTransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      loadingAlert("Loading bank transactions...");
      const token = localStorage.getItem("jwt");
      const response = await api.get("/admin/transactions", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filter bank transactions only
      const bankTransactions = (response.data.data.transactions || []).filter(
        (t) => t.method === "bank"
      );

      setTransactions(bankTransactions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch transactions");
    } finally {
      closeAlert();
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleStatusChange = async (transactionId, newStatus) => {
    try {
      const result = await confirmAlert({
        title: "Confirm Status Change",
        text: `Are you sure you want to mark this transaction as ${newStatus}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update it!",
      });

      if (!result.isConfirmed) return;

      setUpdatingStatus(transactionId);
      const token = localStorage.getItem("jwt");

      await api.patch(
        `/admin/transactions/${transactionId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      successAlert("Success", "Transaction status has been updated");
      await fetchTransactions();
    } catch (err) {
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to update status"
      );
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const result = await confirmAlert({
        title: "Delete Transaction",
        text: "Are you sure you want to delete this transaction? This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (!result.isConfirmed) return;

      loadingAlert("Deleting transaction...");
      const token = localStorage.getItem("jwt");

      await api.delete(`/admin/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      successAlert("Success", "Transaction deleted successfully");
      await fetchTransactions();
    } catch (error) {
      closeAlert();
      errorAlert(
        "Error",
        error.response?.data?.message || "Failed to delete transaction"
      );
    }
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTransaction(null);
  };

  // Filter transactions by search term and status
  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchTerm === "" ||
      transaction._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.user?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.user?.email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.senderName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.accountName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.bank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.bankPaidInto
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.transactionReference
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.accountNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || transaction.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const filteredByType =
    typeFilter === "all"
      ? filteredTransactions
      : filteredTransactions.filter((t) => t.type === typeFilter);

  const paginatedTransactions = filteredByType.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "successful":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "deposit":
        return "success";
      case "withdrawal":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Bank Transactions ({filteredTransactions.length})
        </Typography>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            sx={{ minWidth: 300 }}
            size="small"
            variant="outlined"
            placeholder="Search by name, email, bank, reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FilterIcon sx={{ mr: 1, color: "text.secondary" }} />
              <Select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="successful">Successful</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FilterIcon sx={{ mr: 1, color: "text.secondary" }} />
              <Select
                size="small"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="deposit">Deposits</MenuItem>
                <MenuItem value="withdrawal">Withdrawals</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Bank Details</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      {loading
                        ? "Loading transactions..."
                        : "No bank transactions found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTransactions.map((transaction) => (
                  <TableRow key={transaction._id}>
                    <TableCell>
                      {formatDateTime(transaction.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type || "Unknown"}
                        color={getTypeColor(transaction.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {transaction.user?.name || "N/A"}
                        </Typography>
                        {transaction.user?.email && (
                          <Typography variant="caption" color="text.secondary">
                            {transaction.user.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {transaction.type === "deposit" ? (
                          <>
                            <Typography variant="body2">
                              From: {transaction.bank || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              To: {transaction.bankPaidInto || "N/A"}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              To: {transaction.bank || "N/A"}
                            </Typography>
                            {transaction.accountNumber && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {transaction.accountName} •{" "}
                                {transaction.accountNumber}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        ₦{Number(transaction.amount).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {transaction.transactionReference
                            ? transaction.transactionReference.substring(
                                0,
                                10
                              ) +
                              (transaction.transactionReference.length > 10
                                ? "..."
                                : "")
                            : "N/A"}
                        </Typography>
                        {transaction.transactionReference && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(transaction.transactionReference)
                            }
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={transaction.status}
                        onChange={(e) =>
                          handleStatusChange(transaction._id, e.target.value)
                        }
                        disabled={updatingStatus === transaction._id}
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "4px 8px",
                          },
                          width: "120px",
                        }}
                        renderValue={(value) => (
                          <Chip
                            label={value}
                            color={getStatusColor(value)}
                            size="small"
                            sx={{ height: "24px" }}
                          />
                        )}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="successful">Successful</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(transaction)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {transaction.status !== "pending" && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteTransaction(transaction._id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredByType.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Transaction Details Modal */}
        {detailsOpen && selectedTransaction && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card
              sx={{
                width: "80%",
                maxWidth: "700px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5">
                      {selectedTransaction.type === "withdrawal"
                        ? "Bank Withdrawal Details"
                        : "Bank Deposit Details"}
                    </Typography>
                    <Chip
                      label={selectedTransaction.type || "Unknown"}
                      color={getTypeColor(selectedTransaction.type)}
                      size="small"
                    />
                  </Box>
                  <Button variant="outlined" onClick={handleCloseDetails}>
                    Close
                  </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body1">
                        {selectedTransaction._id}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(selectedTransaction._id)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(selectedTransaction.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      User
                    </Typography>
                    <Typography variant="body1">
                      {selectedTransaction.user?.name || "N/A"}
                      {selectedTransaction.user?.email && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedTransaction.user.email}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₦{Number(selectedTransaction.amount).toLocaleString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {selectedTransaction.type === "deposit" ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Sender Name
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.senderName || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Sender Bank
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.bank || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Admin Bank Paid Into
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.bankPaidInto || "N/A"}
                        </Typography>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Account Name
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.accountName || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Bank Name
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.bank || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Account Number
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransaction.accountNumber || "N/A"}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedTransaction.status}
                      color={getStatusColor(selectedTransaction.status)}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Transaction Reference
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body1">
                        {selectedTransaction.transactionReference || "N/A"}
                      </Typography>
                      {selectedTransaction.transactionReference && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopy(selectedTransaction.transactionReference)
                          }
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>

                  {selectedTransaction.processedBy && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Processed By
                          </Typography>
                          <Typography variant="body1">Admin</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Processed Date
                          </Typography>
                          <Typography variant="body1">
                            {formatDateTime(selectedTransaction.processedAt)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {selectedTransaction.status !== "pending" && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleCloseDetails();
                        handleDeleteTransaction(selectedTransaction._id);
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      Delete Transaction
                    </Button>
                  )}

                  {selectedTransaction.status === "pending" && (
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                          handleStatusChange(
                            selectedTransaction._id,
                            "successful"
                          );
                          handleCloseDetails();
                        }}
                      >
                        Approve Transaction
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                          handleStatusChange(selectedTransaction._id, "failed");
                          handleCloseDetails();
                        }}
                      >
                        Reject Transaction
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default BankTransactionManagement;
